import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  Plus, 
  Filter, 
  Download, 
  Calendar, 
  Bell, 
  FileText, 
  Wrench, 
  User, 
  MapPin, 
  Clock,
  CheckCircle,
  AlertTriangle,
  Edit3,
  Trash2,
  Upload,
  Search,
  X
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import ServiceLogForm from './ServiceLogForm';
import ServiceLogList from './ServiceLogList';
import NotificationCenter from './NotificationCenter';
import ReportGenerator from './ReportGenerator';
import OfflineSync from './OfflineSync';
import { formatZAR } from '../utils/currency';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Toast = ({ message, type, onClose }) => {
  useEffect(() => {
    const timer = setTimeout(onClose, 3000);
    return () => clearTimeout(timer);
  }, [onClose]);

  const bgColor = type === 'success' ? 'bg-green-500' : 
                  type === 'error' ? 'bg-red-500' : 
                  type === 'warning' ? 'bg-yellow-500' : 'bg-blue-500';

  return (
    <div className={`fixed top-4 right-4 ${bgColor} text-white px-6 py-3 rounded-lg shadow-lg z-50 flex items-center space-x-2`}>
      {type === 'success' && <CheckCircle size={20} />}
      {type === 'error' && <AlertTriangle size={20} />}
      {type === 'warning' && <AlertTriangle size={20} />}
      <span>{message}</span>
      <button onClick={onClose} className="ml-2 hover:opacity-80">
        <X size={16} />
      </button>
    </div>
  );
};

const ServiceLogsPage = () => {
  const { user, hasPermission } = useAuth();
  const [logs, setLogs] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingLog, setEditingLog] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [dateRange, setDateRange] = useState({ start: '', end: '' });
  const [showNotifications, setShowNotifications] = useState(false);
  const [showReports, setShowReports] = useState(false);
  const [isOffline, setIsOffline] = useState(!navigator.onLine);
  const [pendingSync, setPendingSync] = useState(0);
  const [notificationCount, setNotificationCount] = useState(0);
  const [toast, setToast] = useState(null);
  
  const showToast = (message, type = 'success') => {
    setToast({ message, type });
  };
  
  const serviceTypes = [
    'Preventive Maintenance',
    'Repair',
    'Inspection',
    'Installation',
    'Emergency',
    'Upgrade',
    'Cleaning'
  ];
  
  const statusTypes = [
    'Scheduled',
    'In Progress',
    'Completed',
    'Overdue',
    'Cancelled'
  ];
  
  const mockLogs = [
    {
      id: 'log-001',
      propertyId: 'prop-001',
      propertyName: 'Downtown Office Complex',
      assetId: 'asset-hvac-001',
      assetName: 'HVAC System - Floor 5',
      serviceType: 'Preventive Maintenance',
      technician: 'John Smith',
      technicianEmail: 'john.smith@maintenance.com',
      scheduledDate: '2023-06-15',
      completedDate: '2023-06-15',
      status: 'Completed',
      priority: 'Medium',
      description: 'Quarterly maintenance of HVAC system including filter replacement and duct cleaning',
      notes: 'System functioning properly after maintenance. Recommended replacement of secondary filter in next 3 months.',
      duration: 180,
      cost: 2500,
      attachments: [
        {
          id: 'att-001',
          name: 'maintenance_report.pdf',
          size: 1024 * 1024 * 2.5,
          type: 'application/pdf',
          url: '/mock-files/maintenance_report.pdf',
          uploaded_at: '2023-06-15T14:30:00Z'
        }
      ]
    },
    {
      id: 'log-002',
      propertyId: 'prop-002',
      propertyName: 'Retail Plaza East',
      assetId: 'asset-elevator-002',
      assetName: 'Passenger Elevator #2',
      serviceType: 'Repair',
      technician: 'Maria Garcia',
      technicianEmail: 'maria.garcia@elevatorsafe.com',
      scheduledDate: '2023-06-20',
      completedDate: null,
      status: 'Scheduled',
      priority: 'High',
      description: 'Elevator door sensor malfunction causing intermittent door closing issues',
      notes: '',
      duration: null,
      cost: null,
      attachments: []
    },
    {
      id: 'log-003',
      propertyId: 'prop-001',
      propertyName: 'Downtown Office Complex',
      assetId: 'asset-security-003',
      assetName: 'Security System - Main Entrance',
      serviceType: 'Inspection',
      technician: 'David Wilson',
      technicianEmail: 'david.wilson@securitypro.com',
      scheduledDate: '2023-05-10',
      completedDate: null,
      status: 'Overdue',
      priority: 'Critical',
      description: 'Annual security system certification and compliance inspection',
      notes: 'Inspection rescheduled twice due to technician availability',
      duration: null,
      cost: 1800,
      attachments: []
    }
  ];
  
  useEffect(() => {
    const fetchServiceLogs = async () => {
      setLoading(true);
      try {
        const response = await fetch(`${API}/service-logs`, {
          credentials: 'include'
        });
        
        if (!response.ok) throw new Error('Failed to fetch logs');
        const serverLogs = await response.json();
        
        setLogs(serverLogs);
        localStorage.setItem('service_logs', JSON.stringify(serverLogs));
        
        localStorage.removeItem('pendingServiceLogs');
        setPendingSync(0);
        
      } catch (error) {
        console.error('Error fetching logs:', error);
        const storedLogs = localStorage.getItem('service_logs');
        if (storedLogs) {
          setLogs(JSON.parse(storedLogs));
        }
      } finally {
        setLoading(false);
      }
    };
    
    const handleOnlineStatus = () => {
      setIsOffline(!navigator.onLine);
    };
    
    window.addEventListener('online', handleOnlineStatus);
    window.addEventListener('offline', handleOnlineStatus);
    
    fetchServiceLogs();
    
    const pendingSyncItems = localStorage.getItem('pending_sync_service_logs');
    if (pendingSyncItems) {
      try {
        const parsedItems = JSON.parse(pendingSyncItems);
        setPendingSync(parsedItems.length);
      } catch (e) {
        setPendingSync(0);
      }
    }
    
    return () => {
      window.removeEventListener('online', handleOnlineStatus);
      window.removeEventListener('offline', handleOnlineStatus);
    };
  }, []);
  
  const filteredLogs = logs.filter(log => {
    const matchesSearch = searchTerm === '' || 
      log.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      log.propertyName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      log.assetName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      log.technician?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesType = filterType === 'all' || log.serviceType === filterType;
    
    const matchesStatus = filterStatus === 'all' || log.status === filterStatus;
    
    const matchesDateRange = 
      (!dateRange.start || new Date(log.scheduledDate) >= new Date(dateRange.start)) &&
      (!dateRange.end || new Date(log.scheduledDate) <= new Date(dateRange.end));
    
    return matchesSearch && matchesType && matchesStatus && matchesDateRange;
  });
  
  const handleSaveServiceLog = async (formData) => {
    try {
      let savedLog;
      
      if (editingLog) {
        const logIndex = logs.findIndex(log => log.id === editingLog.id);
        if (logIndex !== -1) {
          const updatedLog = {
            ...logs[logIndex],
            ...formData,
            id: editingLog.id,
            updated_at: new Date().toISOString()
          };
          
          const updatedLogs = [...logs];
          updatedLogs[logIndex] = updatedLog;
          setLogs(updatedLogs);
          localStorage.setItem('service_logs', JSON.stringify(updatedLogs));
          
          savedLog = updatedLog;
          
          if (navigator.onLine) {
            try {
              const apiData = {
                property_id: updatedLog.propertyId,
                property_name: updatedLog.propertyName,
                asset_id: updatedLog.assetId,
                asset_name: updatedLog.assetName,
                service_type: updatedLog.serviceType,
                technician: updatedLog.technician,
                technician_email: updatedLog.technicianEmail,
                scheduled_date: updatedLog.scheduledDate,
                completed_date: updatedLog.completedDate,
                status: updatedLog.status,
                priority: updatedLog.priority,
                description: updatedLog.description,
                notes: updatedLog.notes,
                duration: updatedLog.duration,
                cost: updatedLog.cost,
                attachments: updatedLog.attachments || []
              };
              
              const response = await fetch(`${API}/service-logs/${editingLog.id}`, {
                method: 'PUT',
                headers: {
                  'Content-Type': 'application/json'
                },
                credentials: 'include',
                body: JSON.stringify(apiData)
              });
              
              if (response.ok) {
                const apiResponse = await response.json();
                showToast('Service log updated successfully');
              } else {
                const errorText = await response.text();
                showToast('Service log saved locally. API sync failed.', 'warning');
              }
            } catch (error) {
              showToast('Service log saved locally. Will sync when online.', 'warning');
            }
          }
          showToast('Service log updated successfully');
        }
      } else {
        const newLog = {
          ...formData,
          id: `log-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        };
        
        const currentLogs = [...logs];
        const updatedLogs = [newLog, ...currentLogs];
        setLogs(updatedLogs);
        localStorage.setItem('service_logs', JSON.stringify(updatedLogs));
        
        savedLog = newLog;
        
        if (navigator.onLine) {
          try {
            const apiData = {
              property_id: newLog.propertyId,
              property_name: newLog.propertyName,
              asset_id: newLog.assetId,
              asset_name: newLog.assetName,
              service_type: newLog.serviceType,
              technician: newLog.technician,
              technician_email: newLog.technicianEmail,
              scheduled_date: newLog.scheduledDate,
              completed_date: newLog.completedDate,
              status: newLog.status,
              priority: newLog.priority,
              description: newLog.description,
              notes: newLog.notes,
              duration: newLog.duration,
              cost: newLog.cost,
              attachments: newLog.attachments || []
            };
            
            const response = await fetch(`${API}/service-logs`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json'
              },
              credentials: 'include',
              body: JSON.stringify(apiData)
            });
            
            if (response.ok) {
              const apiResponse = await response.json();
              
              if (apiResponse.id && apiResponse.id !== newLog.id) {
                const updatedLog = { ...newLog, id: apiResponse.id };
                const finalLogs = [updatedLog, ...currentLogs];
                setLogs(finalLogs);
                localStorage.setItem('service_logs', JSON.stringify(finalLogs));
                savedLog = updatedLog;
              }
              
              showToast('Service log created successfully');
            } else {
              const errorText = await response.text();
              showToast('Service log saved locally. API sync failed.', 'warning');
            }
          } catch (error) {
            showToast('Service log saved locally. Will sync when online.', 'warning');
          }
        } else {
          showToast('Service log saved locally. Will sync when online.', 'warning');
        }
        
        const currentPendingItems = JSON.parse(localStorage.getItem('pending_sync_service_logs') || '[]');
        
        if (navigator.onLine && currentPendingItems.length === 0) {
          showToast('Service log created successfully');
        }
      }
      
      setShowForm(false);
      setEditingLog(null);
      
      return savedLog;
    } catch (error) {
      showToast('Failed to save service log', 'error');
      throw error;
    }
  };
  
  const handleDeleteServiceLog = async (logId) => {
    if (!window.confirm('Are you sure you want to delete this service log?')) {
      return;
    }
    
    try {
      if (navigator.onLine) {
        try {
          const response = await fetch(`${API}/service-logs/${logId}`, {
            method: 'DELETE',
            credentials: 'include'
          });
          
          if (!response.ok) {
            throw new Error('Failed to delete service log');
          }
        } catch (error) {
          console.error('API deletion failed, deleting locally:', error);
          
          const pendingSyncItems = JSON.parse(localStorage.getItem('pending_sync_service_logs') || '[]');
          pendingSyncItems.push({
            action: 'delete',
            data: { id: logId },
            timestamp: new Date().toISOString()
          });
          localStorage.setItem('pending_sync_service_logs', JSON.stringify(pendingSyncItems));
          setPendingSync(pendingSyncItems.length);
        }
      } else {
        const pendingSyncItems = JSON.parse(localStorage.getItem('pending_sync_service_logs') || '[]');
        pendingSyncItems.push({
          action: 'delete',
          data: { id: logId },
          timestamp: new Date().toISOString()
        });
        localStorage.setItem('pending_sync_service_logs', JSON.stringify(pendingSyncItems));
        setPendingSync(pendingSyncItems.length);
      }
      
      const updatedLogs = logs.filter(log => log.id !== logId);
      setLogs(updatedLogs);
      localStorage.setItem('service_logs', JSON.stringify(updatedLogs));
      
      showToast('Service log deleted successfully');
    } catch (error) {
      console.error('Error deleting service log:', error);
      showToast('Failed to delete service log', 'error');
    }
  };
  
  const handleSync = async () => {
    if (!navigator.onLine) {
      showToast('Cannot sync while offline', 'error');
      return;
    }
    
    try {
      const pendingSyncItems = JSON.parse(localStorage.getItem('pending_sync_service_logs') || '[]');
      if (pendingSyncItems.length === 0) {
        showToast('No pending changes to sync');
        return;
      }
      
      let successCount = 0;
      let failCount = 0;
      
      for (const item of pendingSyncItems) {
        try {
          switch (item.action) {
            case 'create':
              await fetch(`${API}/service-logs`, {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json'
                },
                credentials: 'include',
                body: JSON.stringify(item.data)
              });
              successCount++;
              break;
              
            case 'update':
              await fetch(`${API}/service-logs/${item.data.id}`, {
                method: 'PUT',
                headers: {
                  'Content-Type': 'application/json'
                },
                credentials: 'include',
                body: JSON.stringify(item.data)
              });
              successCount++;
              break;
              
            case 'delete':
              await fetch(`${API}/service-logs/${item.data.id}`, {
                method: 'DELETE',
                credentials: 'include'
              });
              successCount++;
              break;
              
            default:
              console.warn('Unknown sync action:', item.action);
              failCount++;
          }
        } catch (error) {
          console.error('Error syncing item:', error, item);
          failCount++;
        }
      }
      
      if (failCount === 0) {
        localStorage.removeItem('pending_sync_service_logs');
        setPendingSync(0);
        showToast(`Successfully synced ${successCount} items`);
      } else {
        showToast(`Synced ${successCount} items, ${failCount} failed`, 'error');
      }
      
      setLoading(true);
      const response = await fetch(`${API}/service-logs`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        setLogs(data);
        localStorage.setItem('service_logs', JSON.stringify(data));
      }
      setLoading(false);
      
    } catch (error) {
      console.error('Error during sync:', error);
      showToast('Sync failed', 'error');
    }
  };
  
  return (
    <div className="space-y-6">
      {toast && (
        <Toast 
          message={toast.message} 
          type={toast.type} 
          onClose={() => setToast(null)} 
        />
      )}
      
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center">
            <Wrench className="mr-3 h-6 w-6 text-cyan-600" />
            Service Logs
          </h1>
          <p className="text-gray-600 mt-1">
            Track and manage maintenance and service activities
          </p>
        </div>
        
        <div className="flex items-center space-x-2">
          {isOffline && (
            <div className="bg-yellow-100 text-yellow-800 px-3 py-1 rounded-lg text-sm flex items-center">
              <AlertTriangle size={16} className="mr-1" />
              Offline Mode
            </div>
          )}
          
          {pendingSync > 0 && (
            <button
              onClick={handleSync}
              disabled={isOffline}
              className="bg-blue-100 text-blue-800 px-3 py-1 rounded-lg text-sm flex items-center disabled:opacity-50"
              title={isOffline ? "Can't sync while offline" : "Sync pending changes"}
            >
              {pendingSync} pending {pendingSync === 1 ? 'change' : 'changes'}
            </button>
          )}
          
          <button
            onClick={() => {
              setEditingLog(null);
              setShowForm(true);
            }}
            className="text-white px-4 py-2 rounded-lg hover:opacity-90 flex items-center gap-2 transition-opacity"
            style={{backgroundColor: '#00C5CB'}}
          >
            <Plus size={16} />
            Add Service Log
          </button>
        </div>
      </div>
      
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          <div className="lg:col-span-2">
            <div className="relative">
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search service logs..."
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
              />
              <Search size={18} className="absolute left-3 top-2.5 text-gray-400" />
            </div>
          </div>
          
          <div>
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
            >
              <option value="all">All Service Types</option>
              {serviceTypes.map(type => (
                <option key={type} value={type}>{type}</option>
              ))}
            </select>
          </div>
          
          <div>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
            >
              <option value="all">All Statuses</option>
              {statusTypes.map(status => (
                <option key={status} value={status}>{status}</option>
              ))}
            </select>
          </div>
          
          <div>
            <button
              onClick={() => setDateRange({ start: '', end: '' })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center justify-center gap-2"
            >
              <Calendar size={16} className="text-gray-400" />
              <span className="text-gray-700">All Dates</span>
            </button>
          </div>
        </div>
      </div>
      
      <ServiceLogList
        logs={filteredLogs}
        loading={loading}
        onEdit={(log) => {
          setEditingLog(log);
          setShowForm(true);
        }}
        onDelete={handleDeleteServiceLog}
      />
      
      {showForm && (
        <ServiceLogForm
          log={editingLog}
          onSave={handleSaveServiceLog}
          onCancel={() => {
            setShowForm(false);
            setEditingLog(null);
          }}
          serviceTypes={serviceTypes}
          statusTypes={statusTypes}
        />
      )}
    </div>
  );
};

export default ServiceLogsPage;